import { getConditionLabel } from "@features/ax/utils";
import { CustomTooltip } from "@shared/components/CustomTooltip";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Activity } from "lucide-react";
import React from "react";

interface AssetKeyStatsCardProps {
  data: any;
}

const getRiskColor = (score: number) => {
  if (score >= 70) return "text-red-600";
  if (score >= 40) return "text-yellow-600";
  return "text-green-600";
};

export const AssetKeyStatsCard: React.FC<AssetKeyStatsCardProps> = ({
  data,
}) => {
  const currency = useCurrency();
  const { label, color } = getConditionLabel(data?.conditionScores);
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-green-600" />
          <CardTitle className="text-lg font-semibold">Key Stats</CardTitle>
        </div>
        <p className="text-sm text-gray-600">
          Performance indicators and health metrics
        </p>
      </CardHeader>
      <CardContent className="space-y-4 pt-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
            <span className="text-sm text-gray-600">Age</span>
            <CustomTooltip message="The age of the asset since its commission date." />
          </div>
          <span className="font-semibold">
            {data?.currentAge ?? "NA"} years
          </span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
            <span className="text-sm text-gray-600">Condition Score</span>
            <CustomTooltip message="The overall physical health of the asset, based on the most recent inspection." />
          </div>
          <div className="text-right">
            <span className={`font-semibold ${color}`}>
              {data?.conditionScores ?? "0"}/100
            </span>
            <span className={`text-sm ml-2 ${color}`}>{label}</span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
            <span className="text-sm text-gray-600">Risk Score</span>
            <CustomTooltip message="The overall risk this asset poses, combining its likelihood of failure with the consequences of that failure." />
          </div>
          <div className="text-right">
            <span
              className={`font-semibold ${getRiskColor(data?.riskScore || 0)}`}
            >
              {data?.riskScore ?? 0}/100
            </span>
            <span
              className={`text-sm ml-2 ${getRiskColor(data?.riskScore || 0)}`}
            >
              {data?.riskStatus}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
            <span className="text-sm text-gray-600">Replacement Cost</span>
            <CustomTooltip message="The estimated capital cost to replace this asset with a new one." />
          </div>
          <span className="font-semibold">
            {currency} {data?.replacementCost ?? "NA"}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};
